import { Injectable } from '@angular/core';
import { FormGroup, FormArray, Validators } from '@angular/forms';

// reactive
import { debounceTime, distinctUntilChanged, map, startWith, takeUntil } from 'rxjs/operators';

// services
import { QuestionService } from './question.service';

// utility
import { find, forEach, get } from 'lodash';
import { Store } from '@ngrx/store';
import { AppState } from '../app-reducer';
import { Observable, Subject } from 'rxjs';

// validators
import {
    validateFutureDate,
    validateMaxAge,
    validateNumber,
    validatePercentageAllocation, 
    validateServicingAgent
} from 'src/app/validators';

@Injectable({
    providedIn: 'root'
})

export class QuestionSubscriptionService {
    appState$: Observable<any>;
    blockName: any;
    questions: any;
    form: FormGroup;
    formData: any;
    BillingMethod: string;
    ReplaceExistingInsuranceOrUseFunds: boolean;
    private _onDestroy = new Subject();


    constructor(
        private qs: QuestionService,
        private store: Store<AppState>
    ) {

        this.appState$ = store.select('sections');

        // get state
        this.appState$.pipe().subscribe((state: any) => {

            // BillingMethod
            this.BillingMethod = get(state, 'content.SECTIONDCoverageDetails.BillingMethod');

            // ReplaceExistingInsuranceOrUseFunds
            this.ReplaceExistingInsuranceOrUseFunds = get(state, 'content.SECTIONEPurposeFundingInformation.ReplaceExistingInsuranceOrUseFunds');

        });
    }

    // fire form subscriptions
    fireSubscriptions(sectionData: any, questions: any, form: FormGroup, formData: any) {
        this.blockName = sectionData.blockName;
        this.questions = questions;
        this.form = form;
        this.formData = formData;
        this._onDestroy.next()

        // console.log(this.blockName);

        if (this.blockName === 'SECTIONAProposedInsured') {
            // add Primary Residence State validation
            this.form.get('PrimaryResidence.State').setValidators(this.qs.getCustomValidator('PrimaryResidenceState', this.formData));

            // // copies PrimaryResidence.State inout to StateOfIssue question, which is currently hidden
            // this.form.get('PrimaryResidence.State').valueChanges.pipe(distinctUntilChanged(),takeUntil(this._onDestroy)).subscribe(val => {
            //     this.form.controls['StateOfIssue'].patchValue(val);
            // });
        }

        if (this.blockName === 'SECTIONBPolicyOwner') {
            this.qs.addAllocationQuestion(this.form, 'totalOwners');
        }

        if (this.blockName === 'SECTIONCBeneficiaryInformation') {
            this.validationSECTIONCBeneficiaryInformation();            
        }

        if (this.blockName === 'SECTIONFExistingReplacementPendingInsuranceInformation') {
            const data = {ReplaceExistingInsuranceOrUseFunds: this.ReplaceExistingInsuranceOrUseFunds};
            const ExistingPolicy = this.form.get('ExistingPolicy').value;
            const ExistingPolicyDetails = this.form.get('ExistingPolicyDetails') as FormArray;

            ExistingPolicyDetails.valueChanges.pipe(
                startWith(ExistingPolicyDetails),
                map((compound) => compound.length),
                distinctUntilChanged()
            ).subscribe(value => {
                forEach(ExistingPolicyDetails.controls, (group: any) => {
                    group.get('ExistingPolicyToBeReplaced').setValidators(this.qs.getCustomValidator('ExistingPolicyToBeReplaced', data));
                });
            });
        }

        if (this.blockName === 'SECTIONJTemporaryLifeInsuranceAgreementApplication') {
            const data = {BillingMethod: this.BillingMethod};
            this.form.controls['TempAgreementWanted'].setValidators(this.qs.getCustomValidator('TempAgreementWanted', data));
        }

        if (this.blockName === 'AgentReport') {
            this.validationAgentReport();
        }
    }

    validationSECTIONCBeneficiaryInformation () {
        this.qs.addAllocationQuestion(this.form, 'PrimaryTotalAllocation');
        this.qs.addAllocationQuestion(this.form, 'SecondaryTotalAllocation');
        const BeneficiaryInformation: FormArray = this.form.get('BeneficiaryInformation') as FormArray;

        BeneficiaryInformation.valueChanges
        .pipe(
            startWith(BeneficiaryInformation),
            map((items) => {
                return items.length;
            }),
            distinctUntilChanged()
        ).subscribe(() => {
            // inner subscription
            forEach(BeneficiaryInformation.controls, (control: any) => {
                // Total Allocation
                control.get('BeneficiaryPrimaryOrSecondary').valueChanges
                .pipe(debounceTime(500), distinctUntilChanged())
                .subscribe((value) => {
                    if (value && this.form.get('SecondaryTotalAllocation')) {
                        const secondary = find(BeneficiaryInformation.value, {BeneficiaryPrimaryOrSecondary: 'Secondary'});

                        if (secondary) {
                            this.form.get('SecondaryTotalAllocation').setValidators([Validators.required, validatePercentageAllocation]);
                            this.form.get('SecondaryTotalAllocation').updateValueAndValidity();
                        } else {
                            this.form.get('SecondaryTotalAllocation').clearValidators();
                            this.form.get('SecondaryTotalAllocation').updateValueAndValidity();
                        }
                    }
                });
            });
        });
    }

    validationAgentReport () {
        this.qs.addAllocationQuestion(this.form, 'totalAllocation');
        const AgentReportAgentInformation = this.form.get('AgentReportAgentInformation') as FormArray;

        AgentReportAgentInformation.valueChanges.pipe(
            startWith(AgentReportAgentInformation),
            map((compound) => compound.length),
            distinctUntilChanged()
        ).subscribe(() => {
            forEach(AgentReportAgentInformation.controls, (group: any) => {
                group.get('AgentReportAgentInformationServAgt').valueChanges.subscribe(value => {
                    let totalYeses = 0;
                    let totalNos = 0;
                    const selectedControlIndex = AgentReportAgentInformation.controls.indexOf(group);

                    if (AgentReportAgentInformation.length > 0) {
                        forEach(AgentReportAgentInformation.controls, (grp: any) => {
                            grp.get('AgentReportAgentInformationServAgt').clearValidators();
                            grp.get('AgentReportAgentInformationServAgt').setValidators([Validators.required]);
                            grp.get('AgentReportAgentInformationServAgt').updateValueAndValidity({emitEvent: false});

                            const srvAgt = grp.get('AgentReportAgentInformationServAgt').value;
                            if (srvAgt === 'true') {
                                totalYeses++;
                            } 
                            if (srvAgt === 'false') {
                                totalNos++;
                            }
                        });
                    }

                    if (totalYeses > 1) {
                        AgentReportAgentInformation.at(selectedControlIndex).get('AgentReportAgentInformationServAgt').setValidators([validateServicingAgent('maxLimit')]);
                        AgentReportAgentInformation.at(selectedControlIndex).get('AgentReportAgentInformationServAgt').updateValueAndValidity({emitEvent: false});
                    }

                    if (totalYeses === 0 && totalNos > 0) {
                        AgentReportAgentInformation.at(selectedControlIndex).get('AgentReportAgentInformationServAgt').setValidators([validateServicingAgent('minRequired')]);
                        AgentReportAgentInformation.at(selectedControlIndex).get('AgentReportAgentInformationServAgt').updateValueAndValidity({emitEvent: false});
                    }
                });
            });
        });
    }
}
